import { useTheme } from '@ihs-widget/stores/useTheme';
import { useEffect, useState, useRef, useContext } from 'react';
import FilterButton from '@ihs-widget/components/StatsConsole/Widgets/common/FilterButton';
import { type IState, StateContext } from "../../../../../state/stateContext";
import { T, SCPlayersActiveFilterName } from "@ihs-widget/utils/enums";
import { useLanguage } from '@ihs-widget/utils/hooks/useLanguage';
import { getEnumKeyByValue, getIsMobileDevice } from '@ihs-widget/utils/functions';
import { LeftArrowInChipsFilters } from '@ihs-widget/components/StatsConsole/Layout/parts/ChipsFiltersarrows/LeftArrowInChipsFilters';
import { RightArrowInChipsFilters } from '@ihs-widget/components/StatsConsole/Layout/parts/ChipsFiltersarrows/RightArrowInChipsFilters';
import { useTranslations } from '../../../../../translation/TranslationContext';

interface IPlayersWidgetProps {
    eventId: string | null;
    statsData: any;
    playersTabData: any;
    menuItemsPLContainerRef: React.RefObject<HTMLDivElement | null>;
}

const FiltersPlayers = ({ statsData, playersTabData, menuItemsPLContainerRef }: IPlayersWidgetProps) => {
    const { theme } = useTheme();

    const ctx = useContext(StateContext);
    const { stContext, setStContext } = ctx || {};
    const { playersTabActiveFilter } = stContext || {};

    const language = useLanguage();
    const t = useTranslations(language);

    const menuItemsPLRef = useRef<HTMLDivElement>(null);
    const [isMenuArrowsVisible, setIsMenuArrowsVisible] = useState(false);
    const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(true);
    const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(true);
    const [isMenuItemsScrollable, setIsMenuItemsScrollable] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const SCPlayersActiveFilter = {
        GOALS: t['InHouseStats.PlayersFilterGoals'],
        ASSISTS: t['InHouseStats.PlayersFilterAssists'],
        CARDS: t['InHouseStats.PlayersFilterCards'],
        SHOTS: t['InHouseStats.PlayersFilterShots']
    } as const;

    type SCPlayersActiveFilterKey = keyof typeof SCPlayersActiveFilter;

    const setActiveFilter = (filter: SCPlayersActiveFilterKey | null) => {
        setStContext((prev: IState) => ({
            ...prev,
            playersTabActiveFilter: filter,
            playersTabActiveFilterName: getActiveFilterName(filter)
        }));
    };

    const getActiveFilterName = (filter: SCPlayersActiveFilterKey | null): SCPlayersActiveFilterName => {
        switch (filter) {
            case getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.GOALS):
                return SCPlayersActiveFilterName.GOALS;
            case getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.ASSISTS):
                return SCPlayersActiveFilterName.ASSISTS;
            case getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.CARDS):
                return SCPlayersActiveFilterName.CARDS;
            case getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.SHOTS):
                return SCPlayersActiveFilterName.SHOTS;
            default:
                return SCPlayersActiveFilterName.GOALS;
        }
    };

    const onClickLeftArrow = () => {
        const menuItemsContainer = menuItemsPLRef.current;
        if (menuItemsContainer) {
            const currentScroll = menuItemsContainer.scrollLeft;
            const scrollAmount = Math.min(40, currentScroll);
            menuItemsContainer.scrollLeft -= scrollAmount;
        }
    }

    const onClickRightArrow = () => {
        const menuItemsContainer = menuItemsPLRef.current;
        if (menuItemsContainer) {
            const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
            const currentScroll = menuItemsContainer.scrollLeft;
            const remainingScroll = maxScroll - currentScroll;
            const scrollAmount = Math.min(40, remainingScroll);
            menuItemsContainer.scrollLeft += scrollAmount;
        }
    }

    useEffect(() => {
        if (playersTabActiveFilter) {
            setStContext((prev: IState) => ({
                ...prev,
                playersTabActiveFilterName: getActiveFilterName(playersTabActiveFilter)
            }));
        } else {
            setStContext((prev: IState) => ({
                ...prev,
                playersTabActiveFilterName: getActiveFilterName(getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.GOALS))
            }));
        }
    }, [playersTabActiveFilter, setStContext]);


    useEffect(() => {
        const isMobileDevice = getIsMobileDevice();
        if (isMobileDevice) {
            setIsMenuArrowsVisible(false);
        } else {
            if (isMenuItemsScrollable) {
                setIsMenuArrowsVisible(true);
            } else {
                setIsMenuArrowsVisible(false);
            }
        }
    }, [isMenuItemsScrollable])

    useEffect(() => {
        const menuItemsContainer = menuItemsPLRef.current;
        if (menuItemsContainer) {
            const handleScroll = () => {
                const currentScroll = menuItemsContainer.scrollLeft;
                const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
                setIsLeftArrowDisabled(currentScroll === 0);
                setIsRightArrowDisabled(Math.abs(currentScroll - maxScroll) < 1);
            };
            
            menuItemsContainer.addEventListener('scroll', handleScroll);
            handleScroll();
            
            return () => {
                menuItemsContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [window.innerWidth]);

    useEffect(() => {
        const checkScrollability = () => {
            if (menuItemsPLContainerRef?.current && menuItemsPLRef?.current) {
                const itemsWidth = menuItemsPLRef.current.scrollWidth;
                const containerWidth = menuItemsPLRef.current.clientWidth;
                setIsMenuItemsScrollable(itemsWidth > containerWidth);
            } else {
                setIsMenuItemsScrollable(false);
            }
        };

        checkScrollability();
        window.addEventListener('resize', checkScrollability);

        return () => {
            window.removeEventListener('resize', checkScrollability);
        };
    }, [language, statsData, playersTabData, menuItemsPLContainerRef]);

    return (
        <div className='flex flex-row justify-center items-center gap-[3px] w-full select-none'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            {isMenuArrowsVisible &&
                <LeftArrowInChipsFilters
                    color={theme === T.LIGHT ? (isLeftArrowDisabled ? '#111111' : '#00531D') : (isLeftArrowDisabled ? '#111111' : '#00531D')}
                    onClick={onClickLeftArrow}
                    isVisibleOnHover={isHovered}
                    isArrowHidden={isLeftArrowDisabled}
                />
            }
            <div ref={menuItemsPLRef}
                className={`pl-[8px] flex min-w-full items-center mt-[8px] mb-[12px]
                                        overflow-x-scroll menuItemsStats-disable-scrollbar z-10 
                                        [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                                        ${isMenuArrowsVisible && 'justify-start'}
                                        ${!isMenuArrowsVisible && isMenuItemsScrollable && 'justify-start'}
                                        ${!isMenuArrowsVisible && !isMenuItemsScrollable && 'justify-center'}`}>
                <FilterButton
                    text={SCPlayersActiveFilter.GOALS}
                    isActive={playersTabActiveFilter === getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.GOALS)}
                    onClick={() => setActiveFilter(getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.GOALS) as SCPlayersActiveFilterKey)}
                    additionalClassName={`mr-[8px]`}
                    id="filter-goals"
                />
                <FilterButton
                    text={SCPlayersActiveFilter.ASSISTS}
                    isActive={playersTabActiveFilter === getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.ASSISTS)}
                    onClick={() => setActiveFilter(getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.ASSISTS) as SCPlayersActiveFilterKey)}
                    additionalClassName={`mr-[8px]`}
                    id="filter-assists"
                />
                <FilterButton
                    text={SCPlayersActiveFilter.CARDS}
                    isActive={playersTabActiveFilter === getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.CARDS)}
                    onClick={() => setActiveFilter(getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.CARDS) as SCPlayersActiveFilterKey)}
                    additionalClassName={`mr-[8px]`}
                    id="filter-cards"
                />
                <FilterButton
                    text={SCPlayersActiveFilter.SHOTS}
                    isActive={playersTabActiveFilter === getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.SHOTS)}
                    onClick={() => setActiveFilter(getEnumKeyByValue(SCPlayersActiveFilter, SCPlayersActiveFilter.SHOTS) as SCPlayersActiveFilterKey)}
                    additionalClassName={`${isMenuItemsScrollable ? 'mr-[8px]' : ''}`}
                    id="filter-shots"
                />
            </div>
            {isMenuArrowsVisible &&
                <RightArrowInChipsFilters
                    color={theme === T.LIGHT ? (isRightArrowDisabled ? '#111111' : '#00531D') : (isRightArrowDisabled ? '#111111' : '#00531D')}
                    onClick={onClickRightArrow}
                    isVisibleOnHover={isHovered}
                    isArrowHidden={isRightArrowDisabled}
                />
            }
        </div>
    )
}

export default FiltersPlayers;
